import Alert, { type AlertColor } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { describeError } from "./errors";

interface Toast {
  id: number;
  message: string;
  severity: AlertColor;
}

interface ToastApi {
  show: (message: string, severity?: AlertColor) => void;
  /** Maps Connect codes to short human copy; never surfaces raw vendor errors. */
  error: (err: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Toast[]>([]);
  const show = useCallback((message: string, severity: AlertColor = "info") => {
    setQueue((q) => [...q, { id: Date.now() + Math.random(), message, severity }]);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({ show, error: (err, fallback) => show(describeError(err, fallback), "error") }),
    [show],
  );
  const current = queue[0];
  const dismiss = () => setQueue((q) => q.slice(1));
  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={current?.id}
        open={current !== undefined}
        autoHideDuration={current?.severity === "error" ? 6000 : 3500}
        onClose={(_, reason) => reason !== "clickaway" && dismiss()}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={current?.severity ?? "info"}
          variant="filled"
          onClose={dismiss}
          sx={{ minWidth: 280 }}
        >
          {current?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
